// /pages/HR/components/kpis/KPIGrid.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import KPICard from './KPICard';
import LocationKPI from './LocationKPI';
import { MARKET_EMOJI_MAP, HR_LOCATIONS } from '../utils/hrConstants';
import './KPIGrid.css';

// Default data - removed satisfaction
const DEFAULT_KPIS = {
  totalTickets: 0,
  completionRate: 0,
  avgDaysOpen: 0
};

// Helper to get location name from code
const getLocationName = (code) => {
  const location = HR_LOCATIONS.find(loc => loc.code === code);
  return location ? location.name : code;
};

// Default locations - using "open" instead of "overdue"
const DEFAULT_LOCATIONS = [
  {
    id: 'FL',
    name: 'Florida',
    open: 0,
    completed: 0,
    trend: 0,
    icon: MARKET_EMOJI_MAP['FL']
  },
  {
    id: 'ME',
    name: 'Maine',
    open: 0,
    completed: 0,
    trend: 0,
    icon: MARKET_EMOJI_MAP['ME']
  },
  {
    id: 'MA',
    name: 'Massachusetts',
    open: 0,
    completed: 0,
    trend: 0,
    icon: MARKET_EMOJI_MAP['MA']
  },
  {
    id: 'NH',
    name: 'New Hampshire',
    open: 0,
    completed: 0,
    trend: 0,
    icon: MARKET_EMOJI_MAP['NH']
  },
  {
    id: 'NY',
    name: 'New York',
    open: 0,
    completed: 0,
    trend: 0,
    icon: MARKET_EMOJI_MAP['NY']
  },
  {
    id: 'RI',
    name: 'Rhode Island',
    open: 0,
    completed: 0,
    trend: 0,
    icon: MARKET_EMOJI_MAP['RI']
  },
  {
    id: 'XX',
    name: 'Other',
    open: 0,
    completed: 0,
    trend: 0,
    icon: MARKET_EMOJI_MAP['XX']
  }
];

const KPIGrid = React.memo(({ 
  data = {}, 
  loading = false,
  onKPIClick = null,
  showLocations = true,
  showLocationHeader = false,
}) => {
  const navigate = useNavigate();

  const [kpiData, setKpiData] = useState(() => ({
    ...DEFAULT_KPIS,
    ...(data.kpis || {})
  }));

  const [locationData, setLocationData] = useState(() => {
    // Always start with all default locations
    if (!data.locations || !Array.isArray(data.locations)) {
      return DEFAULT_LOCATIONS;
    }

    // Create a map of API location data
    const apiLocationMap = new Map();
    data.locations.forEach(loc => {
      const marketCode = loc.market || loc.id || loc.location || 'XX';
      apiLocationMap.set(marketCode, loc);
    });

    // Merge DEFAULT_LOCATIONS with API data
    return DEFAULT_LOCATIONS.map(defaultLoc => {
      const apiData = apiLocationMap.get(defaultLoc.id);
      if (apiData) {
        return {
          id: defaultLoc.id,
          name: defaultLoc.name,
          open: apiData.open ?? apiData.overdue ?? 0,
          completed: apiData.completed ?? 0,
          trend: apiData.trend ?? 0,
          icon: MARKET_EMOJI_MAP[defaultLoc.id] || defaultLoc.icon
        };
      }
      return {
        ...defaultLoc,
        icon: MARKET_EMOJI_MAP[defaultLoc.id] || defaultLoc.icon
      };
    });
  });

  useEffect(() => {
    if (data.kpis) {
      setKpiData(prev => ({
        ...DEFAULT_KPIS,
        ...data.kpis
      }));
    }

    // Always show all default locations, merging in API data where available
    if (data.locations && Array.isArray(data.locations)) {
      // Create a map of API location data by market code
      const apiLocationMap = new Map();
      data.locations.forEach(loc => {
        const marketCode = loc.market || loc.id || loc.location || 'XX';
        apiLocationMap.set(marketCode, loc);
      });

      // Merge DEFAULT_LOCATIONS with API data
      const mergedLocations = DEFAULT_LOCATIONS.map(defaultLoc => {
        const apiData = apiLocationMap.get(defaultLoc.id);
        if (apiData) {
          return {
            id: defaultLoc.id,
            name: defaultLoc.name,
            open: apiData.open ?? apiData.overdue ?? 0,
            completed: apiData.completed ?? 0,
            trend: apiData.trend ?? 0,
            icon: MARKET_EMOJI_MAP[defaultLoc.id] || defaultLoc.icon
          };
        }
        // Return default location with zero values if no API data
        return {
          ...defaultLoc,
          icon: MARKET_EMOJI_MAP[defaultLoc.id] || defaultLoc.icon
        };
      });

      setLocationData(mergedLocations);
    } else if (!data.locations) {
      setLocationData(DEFAULT_LOCATIONS);
    }
  }, [data]);

  const handleKPIClick = useCallback((type) => {
    if (onKPIClick) {
      onKPIClick(type);
    } else {
      const routes = {
        total: '/hr/requests',
        completion: '/hr/requests?status=resolved',
        avgDays: '/hr/requests?sort=duration'
      };
      navigate(routes[type] || '/hr/dashboard');
    }
  }, [onKPIClick, navigate]);

  const handleLocationClick = useCallback((location) => {
    console.log('Location clicked:', location);
    navigate(`/hr/requests?location=${location.id}`);
  }, [navigate]);

  const summaryStats = useMemo(() => {
    const total = locationData.reduce((sum, loc) =>
      sum + (loc.completed || 0) + (loc.open || 0), 0
    );
    const open = locationData.reduce((sum, loc) =>
      sum + (loc.open || 0), 0
    );
    const completed = locationData.reduce((sum, loc) =>
      sum + (loc.completed || 0), 0
    );

    return { total, open, completed };
  }, [locationData]);

  return (
    <div className="kpi-grid-container-simplified">
      {/* Main KPIs Grid */}
      <div className="kpi-main-grid">
        <KPICard
          value={kpiData.totalTickets}
          label="Total Tickets"
          icon={null}  // Removed emoji
          trend={7.8}
          trendLabel="+7.8% vs last week"
          format="number"
          variant="default"
          onClick={() => handleKPIClick('total')}
          loading={loading}
          animate={true}
        />

        <KPICard
          value={kpiData.completionRate}
          label="Completion Rate"
          icon={null}  // Removed emoji
          trend={2.5}
          trendLabel="+2.5% vs last week"
          format="percentage"
          decimals={1}
          variant="success"
          onClick={() => handleKPIClick('completion')}
          loading={loading}
          animate={true}
        />

        <KPICard
          value={kpiData.avgDaysOpen}
          label="Avg. Resolution Time"
          icon={null}
          trend={null}
          trendLabel=""
          format="decimal"
          decimals={1}
          variant="warning"
          onClick={() => handleKPIClick('avgDays')}
          loading={loading}
          animate={true}
        />
      </div>

      {/* Location Section without header */}
      {showLocations && locationData && locationData.length > 0 && (
        <div className="kpi-locations-section-no-header">
          {/* Show header only if prop is true */}
          {showLocationHeader && (
            <div className="kpi-section-header">
              <div className="kpi-section-info">
                <h3 className="kpi-section-title">Location Performance</h3>
                <p className="kpi-section-description">
                  Regional ticket distribution and status
                </p>
              </div>
              <div className="kpi-section-summary">
                <div className="summary-stat">
                  <span className="summary-value">{summaryStats.total}</span>
                  <span className="summary-label">Total</span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-stat completed">
                  <span className="summary-value">{summaryStats.completed}</span>
                  <span className="summary-label">Completed</span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-stat open">
                  <span className="summary-value">{summaryStats.open}</span>
                  <span className="summary-label">Open</span>
                </div>
              </div>
            </div>
          )}

          {/* Location Cards Grid */}
          <div className="kpi-locations-grid">
            {locationData.map((location, index) => {
              const completeLocation = {
                id: location.id || 'UNK',
                name: location.name || 'Unknown Location',
                open: location.open || 0,
                completed: location.completed || 0,
                trend: location.trend || 0,
                icon: location.icon || MARKET_EMOJI_MAP[location.id] || '📍'
              };

              return (
                <LocationKPI
                  key={`${completeLocation.id}-${index}`}
                  location={completeLocation}
                  onClick={() => handleLocationClick(completeLocation)}
                  loading={loading}
                  animate={true}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

KPIGrid.displayName = 'KPIGrid';

export default KPIGrid;